export const STANDARD_ROLE_COLORS = {
  admin: '#ef4444',
  institute_admin: '#ef4444',
  counselor: '#3b82f6',
  receptionist: '#06b6d4',
  accountant: '#10b981',
  hr: '#8b5cf6',
  academic_coordinator: '#f59e0b',
  exam_incharge: '#ec4899',
  librarian: '#6366f1',
  hostel_warden: '#d97706',
  transport_incharge: '#059669',
  teacher: '#10b981',
  student: '#6366f1',
  parent: '#f59e0b',
  staff: '#6b7280',
};

export const getRoleBadgeColor = (roleKeyOrName) => {
  if (!roleKeyOrName) return '#6b7280';
  const key = String(roleKeyOrName).toLowerCase().replace(/[\s-]+/g, '_');
  return STANDARD_ROLE_COLORS[key] || '#6b7280';
};

const normalizePermissions = (source) => {
  if (!source) return [];

  if (Array.isArray(source)) {
    return source
      .flatMap((item) => {
        if (!item) return [];
        if (typeof item === 'string') return [item.trim()];
        if (typeof item === 'object') {
          const module = item.module || item.key;
          if (!module) return [];
          if (Array.isArray(item.actions)) {
            return item.actions.map((action) => `${module}.${action}`);
          }
          return [module];
        }
        return [];
      })
      .filter(Boolean);
  }

  if (typeof source === 'object') {
    return Object.entries(source).flatMap(([module, value]) => {
      if (Array.isArray(value)) {
        return value.map((action) => `${module}.${action}`);
      }
      if (value) return [module];
      return [];
    });
  }

  return [];
};

export const getUserPermissions = (user) => {
  if (!user) return [];
  if (user.role === 'super_admin') return ['*'];

  // /auth/me resolves standard and assigned roles on the server, including denies.
  if (Array.isArray(user.effective_permissions)) {
    return user.effective_permissions;
  }

  const sources = [
    user.permissions,
    user.role_permissions,
    user.custom_role?.permissions,
    user.customRole?.permissions,
  ];

  if (Array.isArray(user.custom_roles)) {
    user.custom_roles.forEach((r) => {
      if (r?.permissions) sources.push(r.permissions);
    });
  }

  const allRaw = sources.flatMap(normalizePermissions);
  const allowSet = new Set();
  const denySet = new Set();

  allRaw.forEach((p) => {
    if (p.startsWith('-') || p.startsWith('!')) {
      denySet.add(p.slice(1));
    } else {
      allowSet.add(p);
    }
  });

  denySet.forEach((denied) => allowSet.delete(denied));
  return Array.from(allowSet);
};

export const hasPermission = (user, permissionKey) => {
  if (!user || !permissionKey) return false;
  if (user.role === 'super_admin') return true;

  // Institute owner with no custom role assigned has full access
  if (user.role === 'institute_admin' && !user.custom_role_id && (!user.custom_role_ids || user.custom_role_ids.length === 0)) {
    return true;
  }

  const permissions = getUserPermissions(user);
  if (permissions.includes('*')) return true;

  if (permissions.includes(permissionKey)) return true;

  // Check module wildcard, e.g. "leads.*" covers "leads.view"
  if (permissionKey.includes('.')) {
    const module = permissionKey.split('.')[0];
    if (permissions.includes(`${module}.*`) || permissions.includes(module)) {
      return true;
    }
  }

  return false;
};

export const hasAnyPermission = (user, permissionKeys = []) => {
  if (!Array.isArray(permissionKeys) || permissionKeys.length === 0) return false;
  return permissionKeys.some((permissionKey) => hasPermission(user, permissionKey));
};

export const usePermission = (user, permissionKey) => ({
  canAccess: hasPermission(user, permissionKey),
});
